import { z } from "zod";

export const insightDistributionSchema = z.object({
  option: z
    .string()
    .describe('The enum option (e.g., "Excellent", "Good", "Fair", "Poor").'),
  count: z
    .number()
    .describe("Number of participants who selected this option."),
  percentage: z
    .number()
    .describe("Percentage of participants who selected this option."),
});

export const floatHistogramBinSchema = z.object({
  min: z.number().describe("Minimum value of the bin range."),
  max: z.number().describe("Maximum value of the bin range."),
  count: z.number().describe("Number of responses in this bin."),
  percentage: z.number().describe("Percentage of responses in this bin."),
  label: z
    .string()
    .describe('Human-readable label for the bin (e.g., "1.0 - 2.0").'),
});

export const floatStatisticsSchema = z.object({
  mean: z.number().describe("Average value of all responses."),
  median: z.number().describe("Middle value when responses are sorted."),
  standardDeviation: z
    .number()
    .describe("Standard deviation showing data spread."),
  actualMin: z
    .number()
    .describe("Minimum value found in the actual response data."),
  actualMax: z
    .number()
    .describe("Maximum value found in the actual response data."),
  absoluteMin: z
    .number()
    .describe(
      "Absolute minimum value possible for this insight (from schema).",
    ),
  absoluteMax: z
    .number()
    .describe(
      "Absolute maximum value possible for this insight (from schema).",
    ),
  q1: z.number().describe("First quartile (25th percentile)."),
  q3: z.number().describe("Third quartile (75th percentile)."),
  iqr: z.number().describe("Interquartile range (Q3 - Q1)."),
});

export const textClusterSchema = z.object({
  theme: z.string().describe('Theme name (e.g., "Ease of Use")'),
  count: z.number().describe("Number of responses in this cluster"),
  percentage: z.number().describe("Percentage of total responses"),
  description: z.string().describe("LLM-generated description of this theme"),
  representativeQuotes: z
    .array(
      z.object({
        quote: z.string().describe("The representative quote text"),
        participantName: z
          .string()
          .describe("Name of the participant who said this quote"),
        interviewUrl: z
          .string()
          .describe("URL to the specific interview where this quote was made"),
        sourceEvidence: z
          .string()
          .optional()
          .describe("Source evidence for this quote"),
      }),
    )
    .max(3)
    .describe("3 most representative quotes with participant attribution"),
  avgConfidence: z.number().describe("Average confidence for this cluster"),
});

export const insightAnalysisGenerationSchema = z.object({
  analysisText: z
    .string()
    .describe(
      "Expert analysis paragraph about the findings from this insight data.",
    ),
  directQuotes: z
    .array(
      z.object({
        quote: z
          .string()
          .describe(
            "Direct quote from PARTICIPANT transcript that supports the analysis (NEVER from interviewer or AI assistant)",
          ),
        participantName: z
          .string()
          .describe(
            'EXACT participant name as provided in the prompt data (e.g., "John Smith"), not generic labels like "Participant 1"',
          ),
        interviewUrl: z
          .string()
          .describe("URL to the specific interview where this quote was made"),
      }),
    )
    .max(3)
    .min(0)
    .describe(
      "0-3 direct quotes from participant transcripts that are descriptive and useful for the analysis",
    ),
});

export const insightComparisonGroupSchema = z.object({
  groupValue: z
    .string()
    .describe(
      'The value for this comparison group (e.g., "25-34", "True", "High")',
    ),
  groupLabel: z.string().describe("Human-readable label for this group"),
  groupCount: z.number().describe("Total number of participants in this group"),
  distribution: z
    .array(insightDistributionSchema)
    .describe("How this group distributed across the primary insight options"),
});

export const insightComparisonSchema = z.object({
  comparisonInsightId: z
    .string()
    .describe("ID of the insight used for comparison"),
  comparisonInsightName: z.string().describe("Name of the comparison insight"),
  comparisonInsightType: z
    .string()
    .describe("Type of the comparison insight (enum, boolean, float, numeric)"),
  totalOverlappingResponses: z
    .number()
    .describe("Number of participants who answered both insights"),
  comparisonGroups: z
    .array(insightComparisonGroupSchema)
    .describe("Breakdown by comparison groups"),
});

export const analysisDetailLevelSchema = z.object({
  nValue: z.number().describe("Number of groupings used for this detail level"),
  levelName: z.enum(["low", "medium", "high"]).describe("Detail level name"),
  distribution: z
    .array(insightDistributionSchema)
    .optional()
    .describe("Distribution for enum/boolean insights at this detail level"),
  histogram: z
    .array(floatHistogramBinSchema)
    .optional()
    .describe("Histogram for float/numeric insights at this detail level"),
  textClusters: z
    .array(textClusterSchema)
    .optional()
    .describe("Text clusters at this detail level"),
  analysisText: z
    .string()
    .describe("Analysis text specific to this detail level"),
  directQuotes: z
    .array(
      z.object({
        quote: z.string().describe("Direct quote from PARTICIPANT transcript"),
        participantName: z.string().describe("EXACT participant name"),
        interviewUrl: z.string().describe("URL to the specific interview"),
        sourceEvidence: z.string().describe("Source evidence for this quote"),
        category: z
          .string()
          .optional()
          .describe("Category/group this quote belongs to (for organization)"),
      }),
    )
    .optional()
    .describe("Direct quotes for this detail level with category organization"),
});

export const insightAnalysisSchema = z.object({
  insightId: z.string().describe("The unique ID of the insight."),
  insightName: z.string().describe("The name of the insight."),
  insightDescription: z
    .string()
    .describe("Description of what this insight measures."),
  insightType: z
    .string()
    .describe("The type of insight (enum, numeric, boolean, text, float)."),
  detailLevels: z
    .object({
      low: analysisDetailLevelSchema.describe(
        "Low detail analysis (fewer, broader categories)",
      ),
      medium: analysisDetailLevelSchema.describe(
        "Medium detail analysis (balanced groupings)",
      ),
      high: analysisDetailLevelSchema.describe(
        "High detail analysis (more granular categories)",
      ),
    })
    .optional()
    .describe("Analysis at different detail levels"),
  distribution: z
    .array(insightDistributionSchema)
    .optional()
    .describe("Distribution of responses for enum/boolean insights."),
  floatStatistics: floatStatisticsSchema
    .optional()
    .describe("Statistical measures for float insights."),
  histogram: z
    .array(floatHistogramBinSchema)
    .optional()
    .describe("Histogram bins for float insights visualization."),
  textClusters: z
    .array(textClusterSchema)
    .optional()
    .describe("Thematic clusters for text insights."),
  clustersFound: z
    .number()
    .optional()
    .describe("Total number of clusters discovered."),
  totalResponses: z
    .number()
    .describe("Total number of valid responses (excluding N/A)."),
  analysisText: z
    .string()
    .describe(
      "Expert analysis paragraph about the findings from this insight data.",
    ),
  directQuotes: z
    .array(
      z.object({
        quote: z
          .string()
          .describe(
            "Direct quote from PARTICIPANT transcript that supports the analysis (NEVER from interviewer or AI assistant)",
          ),
        participantName: z
          .string()
          .describe(
            'EXACT participant name as provided in the prompt data (e.g., "John Smith"), not generic labels like "Participant 1"',
          ),
        interviewUrl: z
          .string()
          .describe("URL to the specific interview where this quote was made"),
        sourceEvidence: z
          .string()
          .describe(
            "The transcript evidence section this quote was extracted from",
          ),
      }),
    )
    .optional()
    .describe(
      "Direct quotes from participant transcripts for non-text insights",
    ),
  comparisonData: insightComparisonSchema
    .optional()
    .describe(
      "Cross-tabulated comparison data when comparing against another insight",
    ),
});

export const textClusterAnalysisSchema = z.object({
  theme: z.string().describe("Concise theme name (2-4 words)"),
  description: z.string().describe("Description of what this theme represents"),
  representativeQuotes: z
    .array(
      z.object({
        quote: z
          .string()
          .describe(
            "The exact quote text from a PARTICIPANT (NEVER from interviewer or AI assistant)",
          ),
        participantName: z
          .string()
          .describe(
            "The name of the participant who said this quote (use the name provided in the prompt)",
          ),
        interviewUrl: z
          .string()
          .describe("URL to the specific interview where this quote was made"),
        sourceEvidence: z
          .string()
          .optional()
          .describe(
            "If the transcript evidence is a JSON structure with message IDs, provide the MESSAGE ID that contains this quote. If it's plain text, copy the relevant text section.",
          ),
      }),
    )
    .max(3)
    .describe(
      "3 most representative quotes that exemplify this theme, with participant attribution",
    ),
  avgConfidence: z
    .number()
    .describe("Average confidence level for this cluster"),
});

export type InsightAnalysis = z.infer<typeof insightAnalysisSchema>;
export type AnalysisDetailLevel = z.infer<typeof analysisDetailLevelSchema>;
export type InsightDistribution = z.infer<typeof insightDistributionSchema>;
export type FloatHistogramBin = z.infer<typeof floatHistogramBinSchema>;
export type FloatStatistics = z.infer<typeof floatStatisticsSchema>;
export type TextCluster = z.infer<typeof textClusterSchema>;
export type InsightAnalysisGeneration = z.infer<
  typeof insightAnalysisGenerationSchema
>;
export type TextClusterAnalysis = z.infer<typeof textClusterAnalysisSchema>;
export type InsightComparison = z.infer<typeof insightComparisonSchema>;
export type InsightComparisonGroup = z.infer<
  typeof insightComparisonGroupSchema
>;
